import { WaveCROpKind, decodeWaveCROpB64, encodeWaveCROpB64, encodeSnapshotB64, emptySnapshot, newBlockId, } from "./blocks.js";
import { WaveBlockKind } from "./types.js";
export class WaveCRDTBridge {
    _blocks = [];
    _appliedSeq = 0;
    _revision = 0;
    seenOpIds = new Set();
    listeners = new Set();
    get blocks() {
        return this._blocks;
    }
    get appliedSeq() {
        return this._appliedSeq;
    }
    get revision() {
        return this._revision;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    notify() {
        this._revision += 1;
        for (const l of this.listeners)
            l();
    }
    constructor(snapshotB64) {
        if (snapshotB64) {
            const snap = decodeSnapshotFromB64(snapshotB64);
            if (snap) {
                this._blocks = sortBlocks(snap.blocks);
            }
        }
    }
    loadSnapshotJSON(snapshot) {
        this._blocks = sortBlocks(snapshot.blocks);
        this.notify();
    }
    exportSnapshotJSON() {
        return { blocks: this._blocks, version: 1 };
    }
    exportSnapshotB64() {
        return encodeSnapshotB64(this.exportSnapshotJSON());
    }
    applyRemoteOps(ops) {
        const sorted = [...ops].sort((a, b) => a.seq - b.seq);
        let touched = false;
        for (const op of sorted) {
            if (this.seenOpIds.has(op.opId))
                continue;
            if (!this.applyRemoteOp(op)) {
                // Yjs binary updates are not block-v1 CRDT — mark seen so polling does not retry forever.
                if (decodeWaveCROpB64(op.dataB64) === null) {
                    this.seenOpIds.add(op.opId);
                    this._appliedSeq = Math.max(this._appliedSeq, op.seq);
                }
                continue;
            }
            this.seenOpIds.add(op.opId);
            this._appliedSeq = Math.max(this._appliedSeq, op.seq);
            touched = true;
        }
        if (touched) {
            this.notify();
        }
    }
    /** Record server-assigned ops after local emit without overwriting in-flight edits. */
    markOpsSeen(ops) {
        for (const op of ops) {
            this.seenOpIds.add(op.opId);
            this._appliedSeq = Math.max(this._appliedSeq, op.seq);
        }
    }
    applyRemoteOp(op) {
        const cro = decodeWaveCROpB64(op.dataB64);
        if (!cro)
            return false;
        this.applyCROp(cro);
        return true;
    }
    mutate(edit) {
        const copy = [...this._blocks];
        edit(copy);
        this._blocks = copy;
        this.reindex();
        const op = encodeWaveCROpB64({
            kind: WaveCROpKind.ReplaceDocument,
            snapshot: this.exportSnapshotJSON(),
        });
        this.notify();
        return op ? [op] : [];
    }
    insertBlock(block, afterBlockId) {
        const opObj = {
            kind: WaveCROpKind.InsertBlock,
            block,
            afterBlockId: afterBlockId ?? undefined,
        };
        const encoded = encodeWaveCROpB64(opObj);
        this.applyCROp(opObj);
        this.notify();
        return encoded ? [encoded] : [];
    }
    updateBlock(block) {
        const opObj = { kind: WaveCROpKind.UpdateBlock, block };
        const encoded = encodeWaveCROpB64(opObj);
        this.applyCROp(opObj);
        this.notify();
        return encoded ? [encoded] : [];
    }
    encodeUpdateBlockOp(block) {
        const encoded = encodeWaveCROpB64({ kind: WaveCROpKind.UpdateBlock, block });
        return encoded ? [encoded] : [];
    }
    deleteBlock(blockId) {
        const opObj = { kind: WaveCROpKind.DeleteBlock, blockId };
        const encoded = encodeWaveCROpB64(opObj);
        this.applyCROp(opObj);
        this.notify();
        return encoded ? [encoded] : [];
    }
    acknowledgeAppliedSeq(seq) {
        this._appliedSeq = Math.max(this._appliedSeq, seq);
    }
    prepareForLoad(snapshotSeq) {
        this._blocks = [];
        this._appliedSeq = snapshotSeq;
        this.seenOpIds.clear();
    }
    static reconstructBlocks(baseline, ops, upToSeq) {
        const bridge = new WaveCRDTBridge();
        return bridge.documentAtSeq(baseline, ops, upToSeq).blocks;
    }
    documentAtSeq(snapshot, ops, upToSeq) {
        let snap = { ...snapshot, blocks: [...snapshot.blocks] };
        const sorted = [...ops].sort((a, b) => a.seq - b.seq);
        for (const op of sorted) {
            if (op.seq > upToSeq)
                continue;
            const cro = decodeWaveCROpB64(op.dataB64);
            if (!cro)
                continue;
            snap = applyCROpToSnapshot(snap, cro);
        }
        return snap;
    }
    applyCROp(op) {
        switch (op.kind) {
            case WaveCROpKind.ReplaceDocument:
                if (op.snapshot) {
                    this._blocks = sortBlocks(op.snapshot.blocks);
                }
                break;
            case WaveCROpKind.InsertBlock:
                if (op.block) {
                    const block = { ...op.block, payload: { ...op.block.payload } };
                    stampNewBlockDates(block);
                    if (op.afterBlockId) {
                        const idx = this._blocks.findIndex((b) => b.id === op.afterBlockId);
                        if (idx >= 0) {
                            this._blocks.splice(idx + 1, 0, block);
                        }
                        else {
                            this._blocks.push(block);
                        }
                    }
                    else {
                        this._blocks.push(block);
                    }
                    this.reindex();
                }
                break;
            case WaveCROpKind.UpdateBlock:
                if (op.block) {
                    const idx = this._blocks.findIndex((b) => b.id === op.block.id);
                    if (idx >= 0) {
                        const block = { ...op.block, payload: { ...op.block.payload } };
                        stampUpdatedBlockDates(block, this._blocks[idx]);
                        this._blocks[idx] = block;
                    }
                }
                break;
            case WaveCROpKind.DeleteBlock:
                if (op.blockId) {
                    this._blocks = this._blocks.filter((b) => b.id !== op.blockId);
                    this.reindex();
                }
                break;
            case WaveCROpKind.MoveBlock:
                if (op.blockId) {
                    const block = this._blocks.find((b) => b.id === op.blockId);
                    if (block) {
                        this._blocks = this._blocks.filter((b) => b.id !== op.blockId);
                        if (op.afterBlockId) {
                            const idx = this._blocks.findIndex((b) => b.id === op.afterBlockId);
                            if (idx >= 0) {
                                this._blocks.splice(idx + 1, 0, block);
                            }
                            else {
                                this._blocks.push(block);
                            }
                        }
                        else {
                            this._blocks.push(block);
                        }
                        this.reindex();
                    }
                }
                break;
        }
    }
    reindex() {
        this._blocks = this._blocks.map((b, i) => ({ ...b, sortIndex: i }));
    }
}
function sortBlocks(blocks) {
    return [...blocks].sort((a, b) => a.sortIndex - b.sortIndex);
}
function decodeSnapshotFromB64(b64) {
    try {
        const json = decodeURIComponent(escape(atob(b64)));
        return JSON.parse(json);
    }
    catch {
        return null;
    }
}
function applyCROpToSnapshot(snap, op) {
    let blocks = [...snap.blocks];
    switch (op.kind) {
        case WaveCROpKind.ReplaceDocument:
            if (op.snapshot)
                return op.snapshot;
            break;
        case WaveCROpKind.InsertBlock:
            if (op.block) {
                const block = { ...op.block, payload: { ...op.block.payload } };
                stampNewBlockDates(block);
                if (op.afterBlockId) {
                    const idx = blocks.findIndex((b) => b.id === op.afterBlockId);
                    if (idx >= 0)
                        blocks.splice(idx + 1, 0, block);
                    else
                        blocks.push(block);
                }
                else {
                    blocks.push(block);
                }
            }
            break;
        case WaveCROpKind.UpdateBlock:
            if (op.block) {
                const idx = blocks.findIndex((b) => b.id === op.block.id);
                if (idx >= 0) {
                    const block = { ...op.block, payload: { ...op.block.payload } };
                    stampUpdatedBlockDates(block, blocks[idx]);
                    blocks[idx] = block;
                }
            }
            break;
        case WaveCROpKind.DeleteBlock:
            if (op.blockId)
                blocks = blocks.filter((b) => b.id !== op.blockId);
            break;
        case WaveCROpKind.MoveBlock:
            if (op.blockId) {
                const block = blocks.find((b) => b.id === op.blockId);
                if (block) {
                    blocks = blocks.filter((b) => b.id !== op.blockId);
                    if (op.afterBlockId) {
                        const idx = blocks.findIndex((b) => b.id === op.afterBlockId);
                        if (idx >= 0)
                            blocks.splice(idx + 1, 0, block);
                        else
                            blocks.push(block);
                    }
                    else {
                        blocks.push(block);
                    }
                }
            }
            break;
    }
    blocks = blocks.map((b, i) => ({ ...b, sortIndex: i }));
    return { blocks, version: snap.version };
}
function isoNow() {
    return new Date().toISOString();
}
function stampNewBlockDates(block) {
    const now = isoNow();
    if (!block.createdAt)
        block.createdAt = now;
    if (!block.updatedAt)
        block.updatedAt = now;
}
function stampUpdatedBlockDates(block, previous) {
    const now = isoNow();
    block.createdAt = previous.createdAt ?? block.createdAt ?? now;
    block.updatedAt = now;
}
export function createDefaultBlock(kind) {
    const id = newBlockId();
    let payload = {};
    switch (kind) {
        case WaveBlockKind.Text:
            payload = { text: "" };
            break;
        case WaveBlockKind.Heading:
            payload = { text: "Titolo", level: 2 };
            break;
        case WaveBlockKind.Decision:
            payload = { title: "Decisione", choices: ["Sì", "No"] };
            break;
        case WaveBlockKind.Signoff:
            payload = { title: "Approvazione", approvals: {} };
            break;
        case WaveBlockKind.Poll:
            payload = {
                title: "Nuovo sondaggio",
                choices: ["Opzione A", "Opzione B", "Opzione C"],
            };
            break;
        case WaveBlockKind.Task:
            payload = { title: "Nuovo task", taskDone: false };
            break;
        case WaveBlockKind.TodoList:
            payload = { title: "Todo", todoItems: [] };
            break;
        case WaveBlockKind.Gallery:
            payload = { title: "Galleria", galleryItems: [] };
            break;
        case WaveBlockKind.TidePageRef:
            payload = { refId: "", refTitle: "Pagina Tide", refApp: "Tide" };
            break;
        case WaveBlockKind.DriftTodoRef:
            payload = { refId: "", refTitle: "Task Drift", refApp: "Drift" };
            break;
        case WaveBlockKind.ReefArticleRef:
            payload = { refId: "", refTitle: "Articolo Reef", refApp: "Reef" };
            break;
        case WaveBlockKind.PulseDocRef:
            payload = { refId: "", refTitle: "Progetto Pulse", refApp: "Pulse" };
            break;
        case WaveBlockKind.File:
            payload = {
                fileName: "",
                mimeType: "application/octet-stream",
                uploadStatus: "uploading",
                ragStatus: "none",
            };
            break;
    }
    return { id, kind, payload, sortIndex: 0 };
}
export { emptySnapshot, newBlockId };
